/**
 * ResponseFunnel: one trapezoid per stage of the response loop, drawn on
 * Recharts (the Wave C catalog's Funnel). The stages are cumulative: a finding
 * that closed also passed through raised, answered and verified.
 *
 * Author: Mahmoud Ragab Ahmed.
 */

import { Funnel, FunnelChart, LabelList, ResponsiveContainer, Tooltip } from "recharts";

import { strings } from "../brand/strings";

import { ChartEmpty, seriesColor, surfaceClass } from "./shared";

/** One funnel stage: the label and the cumulative count that reached it. */
export interface FunnelStage {
  readonly name: string;
  readonly value: number;
}

export function ResponseFunnel({
  ariaLabel,
  data,
  onSelectStage,
}: {
  ariaLabel: string;
  data: readonly FunnelStage[];
  /** Optional click-through (the chart contract): one callback per stage. */
  onSelectStage?: (stage: FunnelStage, index: number) => void;
}): React.JSX.Element {
  const total = data.reduce((sum, stage) => sum + stage.value, 0);
  if (data.length === 0 || total === 0) {
    return <ChartEmpty />;
  }
  const rows = data.map((stage, index) => ({
    name: stage.name,
    value: stage.value,
    fill: seriesColor(index),
  }));
  return (
    <figure className="q-chart" data-testid="response-funnel">
      <div className="q-chart__frame">
        <ResponsiveContainer
          width="100%"
          height="100%"
          initialDimension={{ width: 640, height: 320 }}
        >
          <FunnelChart
            margin={{ top: 16, right: 16, bottom: 16, left: 16 }}
            role="img"
            aria-label={ariaLabel}
            ref={surfaceClass}
          >
            <Tooltip
              isAnimationActive={false}
              content={({ active: hover, payload }) => {
                const row = payload[0]?.payload as Record<string, unknown> | undefined;
                if (!hover || row === undefined) {
                  return null;
                }
                const name = typeof row.name === "string" ? row.name : "";
                const value = typeof row.value === "number" ? row.value : Number(row.value ?? 0);
                return (
                  <div className="q-chart__tooltip">
                    <p className="q-chart__tooltip-title">{strings.charts.mark(name, value)}</p>
                  </div>
                );
              }}
            />
            <Funnel
              dataKey="value"
              data={rows}
              isAnimationActive={false}
              onClick={(_datum, index) => {
                const picked = data[index];
                if (onSelectStage !== undefined && picked !== undefined) {
                  onSelectStage(picked, index);
                }
              }}
            >
              <LabelList
                position="right"
                fill="var(--q-muted)"
                stroke="none"
                dataKey="name"
                fontSize={12}
              />
              <LabelList
                position="inside"
                fill="var(--q-ink)"
                stroke="none"
                dataKey="value"
                fontSize={12}
              />
            </Funnel>
          </FunnelChart>
        </ResponsiveContainer>
      </div>
    </figure>
  );
}
